import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, GripVertical, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/components/feedback/ConfirmDialog";
import { toast } from "@/components/feedback/ToastHost";
import { usePermissions } from "@/hooks/usePermissions";
import { errorMessage } from "@/lib/errors";
import {
  getDayNote,
  getPlannerDay,
  movePlannerLeg,
  movePlannerLoad,
  saveDayNote,
} from "@/services/api/planner";
import { publishDaySnapshot } from "@/services/api/snapshots";

/** Height in px of one hour on the day grid. */
const HOUR_HEIGHT = 56;

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toMinutes(hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  return h * 60 + m;
}

function formatDateLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** A point in grid-local coordinates, used for the leg connector lines. */
interface ConnPoint {
  x: number;
  y: number;
}

interface ConnLine {
  p1: ConnPoint;
  p2: ConnPoint;
}

interface CardBox {
  loadId: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Connect two cards via the midpoints of their closest facing sides.
 * Side-by-side cards join right-edge-centre to left-edge-centre; stacked
 * cards join bottom-edge-centre to top-edge-centre.
 */
function connectBoxes(a: CardBox, b: CardBox): ConnLine & { dist: number } {
  const aCx = a.left + a.width / 2;
  const aCy = a.top + a.height / 2;
  const bCx = b.left + b.width / 2;
  const bCy = b.top + b.height / 2;
  let p1: ConnPoint;
  let p2: ConnPoint;
  if (Math.abs(aCx - bCx) >= Math.abs(aCy - bCy)) {
    // Horizontally separated: face each other on the left/right sides.
    const aLeftOfB = aCx < bCx;
    p1 = { x: aLeftOfB ? a.left + a.width : a.left, y: aCy };
    p2 = { x: aLeftOfB ? b.left : b.left + b.width, y: bCy };
  } else {
    // Stacked: face each other on the top/bottom sides.
    const aAboveB = aCy < bCy;
    p1 = { x: aCx, y: aAboveB ? a.top + a.height : a.top };
    p2 = { x: bCx, y: aAboveB ? b.top : b.top + b.height };
  }
  return { p1, p2, dist: Math.hypot(p2.x - p1.x, p2.y - p1.y) };
}

/**
 * Minimum spanning tree over a load's cards so every leg is linked with the
 * shortest possible connections (no crossings, no duplicate lines).
 */
function connectorLines(cards: CardBox[]): ConnLine[] {
  const n = cards.length;
  if (n < 2) return [];
  const inTree = new Array<boolean>(n).fill(false);
  const minDist = new Array<number>(n).fill(Number.POSITIVE_INFINITY);
  const parent = new Array<number>(n).fill(-1);
  minDist[0] = 0;
  const lines: ConnLine[] = [];
  for (let count = 0; count < n; count++) {
    let u = -1;
    for (let i = 0; i < n; i++) {
      if (!inTree[i] && (u === -1 || minDist[i] < minDist[u])) u = i;
    }
    if (u === -1) break;
    inTree[u] = true;
    if (parent[u] !== -1) {
      const conn = connectBoxes(cards[parent[u]], cards[u]);
      lines.push({ p1: conn.p1, p2: conn.p2 });
    }
    for (let v = 0; v < n; v++) {
      if (!inTree[v]) {
        const d = connectBoxes(cards[u], cards[v]).dist;
        if (d < minDist[v]) {
          minDist[v] = d;
          parent[v] = u;
        }
      }
    }
  }
  return lines;
}

export function LoadPlannerPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [day, setDay] = useState(todayIso());
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState<ReadonlySet<number>>(new Set());
  const [dragging, setDragging] = useState<{ loadId: number; legId: number | null } | null>(
    null,
  );
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [connLines, setConnLines] = useState<ConnLine[]>([]);

  const plan = useQuery({
    queryKey: ["planner", day],
    queryFn: () => getPlannerDay(day),
  });

  const move = useMutation({
    mutationFn: movePlannerLoad,
    onSuccess: () => {
      toast.success("Load moved to new slot");
      void queryClient.invalidateQueries({ queryKey: ["planner", day] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const legMove = useMutation({
    mutationFn: movePlannerLeg,
    onSuccess: () => {
      toast.success("Leg moved to new slot");
      void queryClient.invalidateQueries({ queryKey: ["planner", day] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Per-day schedule note, edited here and shown read-only on the loadboard.
  const noteQuery = useQuery({
    queryKey: ["day-note", day],
    queryFn: () => getDayNote(day),
  });
  const [noteDraft, setNoteDraft] = useState("");
  const [noteDirty, setNoteDirty] = useState(false);
  const savedNote = noteQuery.data?.note ?? "";
  useEffect(() => {
    setNoteDraft(savedNote);
    setNoteDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  // An empty search box clears every highlight, leaving only the site-coloured
  // card borders.
  useEffect(() => {
    if (!search.trim()) {
      setHighlighted(new Set());
    }
  }, [search]);

  const saveNote = useMutation({
    mutationFn: saveDayNote,
    onSuccess: () => {
      toast.success(t("planner.noteSaved"));
      setNoteDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["day-note", day] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const canPublish = has("slots.write");
  const publish = useMutation({
    mutationFn: publishDaySnapshot,
    onSuccess: (result) => {
      toast.success(
        `Published ${result.published} load${result.published === 1 ? "" : "s"} for ${result.day}`,
      );
      void queryClient.invalidateQueries({ queryKey: ["snapshots", day] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const onPublish = async () => {
    const ok = await confirm({
      title: "Publish schedule",
      description: `Snapshot the loads for ${formatDateLabel(day)} and publish them to the loadboard, depot and haulier portals?`,
      confirmLabel: "Publish",
    });
    if (ok) publish.mutate(day);
  };

  const bays = useMemo(() => plan.data?.bays ?? [], [plan.data]);
  const loads = useMemo(() => plan.data?.loads ?? [], [plan.data]);
  const unassigned = useMemo(() => plan.data?.unassigned ?? [], [plan.data]);

  // Distinct sites shown on the planner, with their accent colours (legend).
  const siteLegend = useMemo(() => {
    const bySite = new Map<number, { name: string; color: string | null }>();
    for (const bay of bays) {
      if (!bySite.has(bay.site_id)) {
        bySite.set(bay.site_id, { name: bay.site_name, color: bay.site_color });
      }
    }
    return Array.from(bySite.entries()).map(([siteId, site]) => ({ siteId, ...site }));
  }, [bays]);

  // Draw a connector between the closest sides of each load's leg cards.
  const recomputeLines = useCallback(() => {
    const container = gridRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const byLoad = new Map<number, CardBox[]>();
    for (const el of Array.from(container.querySelectorAll<HTMLElement>("[data-load-id]"))) {
      const loadId = Number(el.dataset.loadId);
      const rect = el.getBoundingClientRect();
      const box: CardBox = {
        loadId,
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
        width: rect.width,
        height: rect.height,
      };
      const group = byLoad.get(loadId);
      if (group) group.push(box);
      else byLoad.set(loadId, [box]);
    }
    const lines: ConnLine[] = [];
    for (const group of byLoad.values()) {
      if (group.length >= 2) lines.push(...connectorLines(group));
    }
    setConnLines((prev) => {
      const same =
        prev.length === lines.length &&
        prev.every(
          (line, index) =>
            line.p1.x === lines[index].p1.x &&
            line.p1.y === lines[index].p1.y &&
            line.p2.x === lines[index].p2.x &&
            line.p2.y === lines[index].p2.y,
        );
      return same ? prev : lines;
    });
  }, []);

  useLayoutEffect(() => {
    recomputeLines();
    const onResize = () => recomputeLines();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [recomputeLines, loads, unassigned]);

  // Global day range (min window start .. max window end across all bays).
  const range = useMemo(() => {
    let start = Infinity;
    let end = -Infinity;
    for (const bay of bays) {
      for (const slot of bay.slots) {
        start = Math.min(start, toMinutes(slot.start_local));
        end = Math.max(end, toMinutes(slot.end_local));
      }
    }
    if (!Number.isFinite(start)) {
      start = 8 * 60;
      end = 20 * 60;
    }
    return { start, end };
  }, [bays]);

  const gridHeight = ((range.end - range.start) * HOUR_HEIGHT) / 60;
  const topPx = (minutes: number) => ((minutes - range.start) * HOUR_HEIGHT) / 60;
  const heightPx = (minutes: number) => (minutes * HOUR_HEIGHT) / 60;

  const hourLabels: { hour: number; topPx: number }[] = [];
  for (let minutes = range.start; minutes < range.end; minutes += 60) {
    hourLabels.push({ hour: minutes / 60, topPx: topPx(minutes) });
  }

  const isOccupied = (bayId: number, startLocal: string, endLocal: string) =>
    loads.some(
      (load) =>
        load.bay_id === bayId &&
        toMinutes(load.start_local) < toMinutes(endLocal) &&
        toMinutes(load.end_local) > toMinutes(startLocal),
    );

  const onSearch = () => {
    const query = search.trim().toLowerCase();
    if (!query) return;
    const all = [...loads, ...unassigned];
    // Highlight every card (all legs) whose load matches the search term.
    const matched = new Set<number>();
    let first: (typeof all)[number] | undefined;
    for (const load of all) {
      const hit = [
        load.load_ref,
        load.customer_name,
        load.delivery_time,
        load.destination_address,
        load.postcode,
        load.haulier_name,
      ].some((value) => (value ?? "").toLowerCase().includes(query));
      if (hit) {
        matched.add(load.load_id);
        first ??= load;
      }
    }
    if (matched.size === 0) {
      setHighlighted(new Set());
      toast.error(`No load found for “${search.trim()}”`);
      return;
    }
    setHighlighted(matched);
    if (first) {
      setTimeout(() => {
        document
          .querySelector(`[data-load-id="${first!.load_id}"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 60);
    }
  };

  const nowMinutes = useMemo(() => {
    if (plan.data?.date !== todayIso()) return null;
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, [plan.data?.date]);

  const moveTo = (
    loadId: number,
    legId: number | null,
    bayId: number,
    startLocal: string,
    endLocal: string,
  ) => {
    const card = [...loads, ...unassigned].find((l) => l.load_id === loadId);
    if (!card) return;
    const bay = bays.find((b) => b.bay_id === bayId);
    const slot = bay?.slots.find(
      (s) => s.start_local === startLocal && s.end_local === endLocal,
    );
    if (!slot) return;
    const sameSlot =
      "bay_id" in card &&
      card.bay_id === bayId &&
      card.start_local === startLocal &&
      card.end_local === endLocal;
    if (sameSlot) return;
    if (legId != null) {
      // Move just this leg (site + collection time); the load stays put.
      legMove.mutate({
        leg_id: legId,
        bay_id: bayId,
        start_utc: slot.start_utc,
        end_utc: slot.end_utc,
      });
    } else {
      move.mutate({
        load_id: loadId,
        bay_id: bayId,
        start_utc: slot.start_utc,
        end_utc: slot.end_utc,
      });
    }
  };

  /** Compact load-card body shared by bay and unassigned cards. */
  const renderCardBody = (
    load: {
      load_id: number;
      leg_id: number | null;
      load_ref: string;
      direction: string;
      collection_reference: string | null;
      customer_name: string;
      delivery_time: string | null;
      departure_time: string | null;
      destination_address: string | null;
      postcode: string | null;
      carton_count: number | null;
      pallet_count: number | null;
      haulier_name: string | null;
      haulier_phone: string | null;
    },
    showCollectionRef = false,
  ) => {
    const openLoadEditor = () => {
      const page = load.direction === "collection" ? "collections" : "loads";
      navigate(`/admin/${page}?edit=${load.load_id}`);
    };
    const depMinutes = load.departure_time ? toMinutes(load.departure_time) : null;
    const late = nowMinutes !== null && depMinutes !== null && depMinutes < nowMinutes;
    const reference = showCollectionRef
      ? (load.collection_reference ?? load.load_ref)
      : load.load_ref;
    return (
      <>
        {/* Line 1: grip, load no, customer (…) · Depart time */}
        <div className="flex items-center gap-1">
          <span
            draggable
            onDragStart={() => setDragging({ loadId: load.load_id, legId: load.leg_id })}
            onDragEnd={() => setDragging(null)}
            className="shrink-0 cursor-grab text-muted-foreground"
            title="Drag to schedule"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
          <span
            className="shrink-0 cursor-pointer font-semibold underline decoration-dotted underline-offset-2 hover:text-primary"
            title={t("planner.openLoad")}
            onDoubleClick={openLoadEditor}
          >
            {reference}
          </span>
          <span className="min-w-0 truncate font-medium text-muted-foreground">
            {load.customer_name}
          </span>
          <span
            className={`ml-auto shrink-0 ${
              late ? "font-bold text-red-500" : "text-muted-foreground"
            }`}
          >
            {load.departure_time ?? "—"}
          </span>
        </div>
        {/* Line 2: address · Arrive time */}
        <div className="flex items-center gap-1">
          <span className="min-w-0 truncate text-muted-foreground">
            {load.destination_address ?? "—"}
          </span>
          <span className="ml-auto shrink-0 text-muted-foreground">
            {load.delivery_time ?? "—"}
          </span>
        </div>
        {/* Line 3: postcode · cartons (centre) · pallets */}
        <div className="flex items-center gap-1">
          <span className="truncate text-muted-foreground">{load.postcode ?? ""}</span>
          <span className="flex-1 text-center text-muted-foreground">
            {load.carton_count != null ? `${load.carton_count} cartons` : ""}
          </span>
          <span className="ml-auto shrink-0 text-muted-foreground">
            {load.pallet_count != null ? `${load.pallet_count} pallets` : ""}
          </span>
        </div>
        {/* Line 4: haulier · company phone */}
        <div className="flex items-center gap-1">
          <span className="min-w-0 truncate text-muted-foreground">
            {load.haulier_name ?? "—"}
          </span>
          <span className="ml-auto shrink-0 text-muted-foreground">
            {load.haulier_phone ?? ""}
          </span>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t("nav.loadScheduler")}</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setDay(addDays(day, -1))}
          aria-label="Previous day"
          title="Previous day"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setDay(addDays(day, 1))}
          aria-label="Next day"
          title="Next day"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="min-w-0 text-lg font-semibold">{formatDateLabel(day)}</div>
        <Input
          type="date"
          value={day}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          className="w-40"
          aria-label="Pick a day"
        />
        <div className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5">
          <label htmlFor="day-note" className="shrink-0 text-sm font-semibold">
            {t("planner.notes")}
          </label>
          <Textarea
            id="day-note"
            value={noteDraft}
            onChange={(e) => {
              setNoteDraft(e.target.value);
              setNoteDirty(true);
            }}
            rows={2}
            placeholder={t("planner.notePlaceholder")}
            className="h-12 w-72 min-h-[2.5rem] resize-y"
          />
          <Button
            size="sm"
            onClick={() => saveNote.mutate({ day, note: noteDraft.trim() || null })}
            disabled={saveNote.isPending || !noteDirty}
            className="shrink-0"
          >
            {t("planner.saveNote")}
          </Button>
        </div>
        <div className="ml-auto flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="Search Calendar"
              className="w-64"
            />
            <Button variant="outline" size="icon" onClick={onSearch} aria-label="Search">
              <Search className="h-4 w-4" />
            </Button>
          </div>
          {siteLegend.length > 0 ? (
            <div className="flex max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {siteLegend.map((site) => (
                <span key={site.siteId} className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-3 w-3 rounded-full border"
                    style={{ backgroundColor: site.color ?? "var(--color-border)" }}
                    aria-hidden
                  />
                  {site.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {plan.isLoading ? (
        <div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <div className="min-w-max">
            <div
              ref={gridRef}
              onMouseOver={() => requestAnimationFrame(recomputeLines)}
              className="relative grid"
              style={{ gridTemplateColumns: `56px repeat(${Math.max(1, bays.length)}, minmax(200px, 1fr)) minmax(220px, 1fr)` }}
            >
              {/* Header row */}
              <div />
              {bays.map((bay) => (
                <div key={bay.bay_id} className="border-b bg-muted/60 px-2 py-1.5 text-sm font-semibold">
                  {bay.name}
                  <div className="text-xs font-normal text-muted-foreground">
                    {bay.site_name} · {bay.slot_duration_minutes} min slots
                  </div>
                </div>
              ))}
              <div className="border-b bg-muted/60 px-2 py-1.5 text-sm font-semibold">
                Unassigned
              </div>

              {/* Time ruler + bay columns */}
              <div className="relative" style={{ height: gridHeight }}>
                {hourLabels.map((label) => (
                  <div
                    key={label.hour}
                    className="absolute right-1 -translate-y-1/2 text-xs text-muted-foreground"
                    style={{ top: label.topPx }}
                  >
                    {String(label.hour).padStart(2, "0")}
                  </div>
                ))}
              </div>

              {bays.map((bay) => (
                <div key={bay.bay_id} className="relative border-l bg-background" style={{ height: gridHeight }}>
                  {/* Hour stripes */}
                  {hourLabels.map((label) => (
                    <div
                      key={label.hour}
                      className={`pointer-events-none absolute inset-x-0 border-t ${
                        label.hour % 2 === 0 ? "border-muted" : "border-muted/40"
                      }`}
                      style={{ top: label.topPx }}
                    />
                  ))}

                  {/* Slot drop targets */}
                  {bay.slots.map((slot, index) => {
                    const occupied = isOccupied(bay.bay_id, slot.start_local, slot.end_local);
                    return (
                      <div
                        key={index}
                        className={`absolute inset-x-0 border-b border-dashed ${
                          occupied ? "bg-muted/20" : ""
                        }`}
                        style={{
                          top: topPx(toMinutes(slot.start_local)),
                          height: heightPx(toMinutes(slot.end_local) - toMinutes(slot.start_local)),
                        }}
                        onDragOver={(e) => {
                          if (!occupied) e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragging == null || occupied) return;
                          moveTo(
                            dragging.loadId,
                            dragging.legId,
                            bay.bay_id,
                            slot.start_local,
                            slot.end_local,
                          );
                        }}
                      />
                    );
                  })}

                  {/* Load cards */}
                  {loads
                    .filter((load) => load.bay_id === bay.bay_id)
                    .map((load) => {
                      const duration = toMinutes(load.end_local) - toMinutes(load.start_local);
                      const isHighlighted = highlighted.has(load.load_id);
                      return (
                        <div
                          id={`planner-leg-${load.leg_id ?? load.load_id}`}
                          data-load-id={load.load_id}
                          data-leg-id={load.leg_id ?? load.load_id}
                          key={load.leg_id ?? load.load_id}
                          className={`group absolute inset-x-0.5 z-10 h-[var(--slot-h)] overflow-hidden rounded border bg-card p-1.5 text-xs shadow-sm transition hover:h-auto hover:z-30 ${
                            dragging?.loadId === load.load_id ? "opacity-60" : ""
                          } ${
                            isHighlighted
                              ? "ring-2 ring-primary ring-offset-1"
                              : "hover:shadow"
                          }`}
                          style={
                            {
                              top: topPx(toMinutes(load.start_local)),
                              ["--slot-h"]: `${heightPx(duration)}px`,
                              borderColor: load.site_color ?? undefined,
                            } as CSSProperties
                          }
                        >
                          <div className="line-clamp-3 group-hover:line-clamp-none">
                            {renderCardBody(load, true)}
                          </div>
                        </div>
                      );
                    })}

                  {/* Current-time marker (only on today's view) */}
                  {nowMinutes !== null &&
                    nowMinutes >= range.start &&
                    nowMinutes <= range.end && (
                      <div
                        className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-red-500"
                        style={{ top: topPx(nowMinutes) }}
                      >
                        <div className="absolute -left-0.5 -top-1 h-2 w-2 rounded-full bg-red-500" />
                      </div>
                    )}
                </div>
              ))}

              {/* Unassigned column: non-draft loads for the day with no bay slot */}
              <div className="border-l bg-background p-2" style={{ height: gridHeight }}>
                <div className="space-y-2 overflow-y-auto pr-1" style={{ height: "100%" }}>
                  {unassigned.map((load) => (
                    <div
                      key={load.leg_id ?? load.load_id}
                      id={`planner-leg-${load.leg_id ?? load.load_id}`}
                      data-load-id={load.load_id}
                      data-leg-id={load.leg_id ?? load.load_id}
                      className={`group rounded border bg-muted/10 p-2 text-xs shadow-sm ${
                        highlighted.has(load.load_id)
                          ? "ring-2 ring-primary ring-offset-1"
                          : ""
                      }`}
                      style={{ borderColor: load.site_color ?? undefined }}
                    >
                      <div className="line-clamp-3 group-hover:line-clamp-none">
                        {renderCardBody(load)}
                      </div>
                    </div>
                  ))}
                  {unassigned.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">
                      No unassigned loads
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Connector lines between the legs of the same load */}
              {connLines.length > 0 ? (
                <svg
                  className="pointer-events-none absolute inset-0 z-[5] h-full w-full"
                  aria-hidden
                >
                  {connLines.map((line, index) => (
                    <line
                      key={index}
                      x1={line.p1.x}
                      y1={line.p1.y}
                      x2={line.p2.x}
                      y2={line.p2.y}
                      stroke="var(--color-primary)"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeDasharray="4 3"
                      opacity={0.7}
                    />
                  ))}
                </svg>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {canPublish ? (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={() => void onPublish()}
            disabled={publish.isPending}
            size="icon"
            className="h-14 w-14 rounded-full shadow-lg"
            title={t("planner.publish")}
            aria-label={t("planner.publish")}
          >
            <img
              src="/images/publish.svg"
              alt=""
              className={`h-6 w-6 ${publish.isPending ? "animate-pulse" : ""}`}
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
